import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { AnimatePresence, LayoutGroup, motion, MotionConfig } from 'motion/react';
import { ArrowRightIcon, FunnelIcon, ShieldCheckIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { RentalOfferCard, type RentalOfferView } from '@voll-intelligence/intelligence-ui-kit';
import {
  type ChatUiRentalOffer,
  type ChatUiRentalSearchMeta,
  type ChatUiRentalTravelerPolicy,
  type RentalOfferPageHint,
  RENTAL_INLINE_OFFERS,
} from '@voll-intelligence/genui-contract';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { isHiddenInStack, stackStagger, useStackedDeck } from '../../../shared/ui/stacked_deck.js';
import {
  CAROUSEL_RAIL,
  CAROUSEL_SLIDE,
  CarouselDots,
} from '../../../flight/shared/offer_carousel.js';
import { useOfferLayout, type OfferLayout } from '@voll-intelligence/intelligence-ui-kit';
import { RentalResultsDialog } from './rental_results_dialog.js';
import { RentalPolicyDrawer } from './components/policy_drawer.js';
import { rentalShortlistViews } from './rental_offer_views.js';
import { rentalVehicleCopy } from '../../shared/rental_vehicle_copy.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';

// Curva e duração do encolhimento lista → pilha (a mesma sensação do deck de
// viagens). O `motion` faz o FLIP de cada card (prop `layout`); o stagger é o
// atraso, de baixo para cima ao recolher.
const GLIDE = { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] as const };

/** A seleção recebida: a oferta, a posição dela na lista em que foi tocada e,
 *  vinda de "Ver todos os carros", a página que a trouxe (#5833). */
type SelectOffer = (offer: ChatUiRentalOffer, position: number, page?: RentalOfferPageHint) => void;

/**
 * Os carros do cartão: os `RENTAL_INLINE_OFFERS` primeiros, na ordem do agente
 * (a seleção curta vem na frente). No recibo, o escolhido sobe ao topo — também
 * quando veio de "Ver todos" e mora além dos três, se o bloco o traz.
 */
function cardOffersOf(
  offers: readonly ChatUiRentalOffer[],
  chosenId: string | undefined,
): ChatUiRentalOffer[] {
  const chosen = chosenId ? offers.find((offer) => offer.offerId === chosenId) : undefined;
  if (!chosen) return offers.slice(0, RENTAL_INLINE_OFFERS);
  const rest = offers.filter((offer) => offer.offerId !== chosen.offerId);
  return [chosen, ...rest].slice(0, RENTAL_INLINE_OFFERS);
}

/** O nome acessível e o gesto da pilha congelada: o deck inteiro abre e fecha
 *  (o mesmo do deck de viagens); com um card só, não há o que abrir. */
function deckToggleProps(
  toggleable: boolean,
  expanded: boolean,
  label: string,
  toggle: () => void,
): Pick<
  HTMLAttributes<HTMLDivElement>,
  'role' | 'tabIndex' | 'aria-expanded' | 'aria-label' | 'onClick' | 'onKeyDown'
> {
  if (!toggleable) return { role: 'list' };
  return {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': expanded,
    'aria-label': label,
    onClick: toggle,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
  };
}

/** A pilha do recibo no desenho (Figma, frame 05, nó 5422:27052): cada card de
 *  trás é o MESMO card, 2% menor por nível e 16px mais baixo, então o pé dele
 *  (o porquê) aparece numa faixa sob o de cima. */
const STACK_STEP_PX = 16;
const STACK_SCALE_STEP = 0.02;

/** A posição de cada card: na lista, o fluxo; na pilha, absoluto no topo, e os
 *  de trás na altura do de cima, com o próprio card alinhado pelo pé e cortado
 *  só em cima (a sombra e os cantos de baixo ficam). A sombra é a do próprio
 *  card — a elevação do bloco da thread, a mesma na lista e na pilha (#6296). */
function deckCardClass(stacked: boolean, peek: boolean): string {
  if (!stacked) return 'relative';
  if (!peek) return 'absolute inset-x-0 top-0';
  return 'absolute inset-x-0 top-0 flex h-(--stack-card-h) flex-col justify-end [clip-path:inset(0_-24px_-24px_-24px)]';
}

/** O deslocamento de um card de trás: um degrau para baixo e menor por nível,
 *  ancorado no pé, que é o que a faixa mostra. */
function stackMotion(index: number, stacked: boolean) {
  const level = stacked ? Math.min(index, 2) : 0;
  return { y: level * STACK_STEP_PX, scale: 1 - level * STACK_SCALE_STEP };
}

/**
 * A lista do desktop, viva ou congelada, numa árvore só: os mesmos `motion.div`
 * por oferta nos dois estados, para o recibo ENCOLHER a lista em pilha (FLIP de
 * cada card, o escolhido no topo e os outros deslizando para trás dele,
 * encolhendo um degrau por nível) em vez de trocar uma árvore pela outra. Na
 * pilha os cards de trás ficam fora da árvore de acessibilidade.
 */
function RentalDeckList({
  views,
  layout,
  chosenId,
  deck,
  frozen,
  onSelect,
}: Readonly<{
  views: readonly RentalOfferView[];
  layout: OfferLayout;
  chosenId: string | undefined;
  deck: ReturnType<typeof useStackedDeck>;
  frozen: boolean;
  onSelect: (offerId: string) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { expanded, stacked, listRef, toggle } = deck;
  const copy = { selectLabel: t('rental.rent'), ...rentalVehicleCopy(t) };
  const toggleable = frozen && views.length > 1;
  return (
    <motion.div
      layout
      ref={listRef}
      className={`relative mt-3 grid gap-5 ${stacked ? 'cursor-pointer' : ''}`}
      {...deckToggleProps(toggleable, expanded, t('rental.deckToggleAria'), toggle)}
    >
      {views.map((view, index) => {
        const peek = stacked && index > 0;
        const delay = stackStagger(index, views.length, expanded);
        return (
          <motion.div
            key={view.id}
            layout="position"
            data-deck-card=""
            role={toggleable ? undefined : 'listitem'}
            // A marca do escolhido é semântica: o desenho do recibo não tem anel.
            aria-current={view.id === chosenId || undefined}
            className={deckCardClass(stacked, peek)}
            style={{ zIndex: views.length - index, originY: 1 }}
            aria-hidden={peek || undefined}
            initial={false}
            animate={{
              ...stackMotion(index, stacked),
              opacity: stacked && isHiddenInStack(index) ? 0 : 1,
            }}
            transition={{
              layout: { ...GLIDE, delay },
              y: { ...GLIDE, delay },
              scale: { ...GLIDE, delay },
              opacity: { duration: 0.3, delay },
            }}
          >
            <RentalOfferCard offer={view} copy={copy} layout={layout} onSelect={onSelect} />
          </motion.div>
        );
      })}
    </motion.div>
  );
}

/**
 * O celular (#5125): os cards num carrossel com snap e os pontos embaixo, como
 * o voo; no recibo o carrossel some e fica só o escolhido — os outros saem
 * esmaecendo e ele assenta no lugar.
 */
function RentalRail({
  views,
  chosenId,
  receipt,
  onSelect,
}: Readonly<{
  views: readonly RentalOfferView[];
  chosenId: string | undefined;
  receipt: boolean;
  onSelect: (offerId: string) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const railRef = useRef<HTMLUListElement>(null);
  const copy = { selectLabel: t('rental.rent'), ...rentalVehicleCopy(t) };
  const shown = receipt ? views.filter((view) => view.id === chosenId) : views;
  return (
    <>
      <ul
        ref={railRef}
        className={receipt ? 'm-0 grid list-none p-0 pt-3' : `${CAROUSEL_RAIL} m-0 list-none pt-3`}
      >
        <AnimatePresence initial={false}>
          {shown.map((view) => (
            <motion.li
              key={view.id}
              layout="position"
              className={receipt ? 'min-w-0' : CAROUSEL_SLIDE}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ layout: GLIDE, opacity: { duration: 0.2 } }}
            >
              <RentalOfferCard
                offer={view}
                copy={copy}
                layout="stack"
                // Fora de vista no trilho, a foto lazy só pediria rede no swipe
                // (e nunca, para quem espera a página assentar).
                photoLoading="eager"
                selected={view.id === chosenId}
                onSelect={onSelect}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {!receipt && (
        <CarouselDots railRef={railRef} count={shown.length} slideKey="rentalOffers.slideAria" />
      )}
    </>
  );
}

/** O rodapé: "Ver suas políticas" e "Ver todos os carros" — à direita no
 *  desktop, empilhados e na largura toda no celular (o "Ver todos" primeiro). */
function RentalOffersFooter({
  layout,
  hasPolicies,
  onPolicies,
  onSeeAll,
}: Readonly<{
  layout: OfferLayout;
  hasPolicies: boolean;
  onPolicies: () => void;
  onSeeAll: (() => void) | undefined;
}>): ReactNode {
  const { t } = useGenUiI18n();
  if (!hasPolicies && !onSeeAll) return null;
  const stack = layout === 'stack';
  const policies = hasPolicies && (
    <Button type="button" variant="ghost" size={stack ? 'xl' : 'lg'} onClick={onPolicies}>
      <ShieldCheckIcon aria-hidden="true" />
      {t('rentalOffers.viewPolicies')}
    </Button>
  );
  const seeAll = onSeeAll && (
    <Button type="button" variant="outline" size={stack ? 'xl' : 'lg'} onClick={onSeeAll}>
      <FunnelIcon aria-hidden="true" />
      {t('rentalOffers.seeAll')}
      <ArrowRightIcon aria-hidden="true" />
    </Button>
  );
  return (
    <motion.div
      layout="position"
      transition={{ layout: GLIDE }}
      className={stack ? 'grid gap-2 [&>*]:w-full' : 'flex justify-end gap-3'}
    >
      {stack ? (
        <>
          {seeAll}
          {policies}
        </>
      ) : (
        <>
          {policies}
          {seeAll}
        </>
      )}
    </motion.div>
  );
}

/**
 * O rodapé e o que ele abre: "Ver todos os carros" (a página da bancada em
 * diálogo, paginada pelo BFF via `rentalRefine`) e "Ver suas políticas" (a
 * gaveta das políticas do viajante).
 */
function RentalOffersActions({
  offers,
  metadata,
  searchContext,
  policies,
  layout,
  onSelect,
}: Readonly<{
  offers: ChatUiRentalOffer[];
  metadata: ChatUiRentalSearchMeta;
  searchContext: string;
  policies: ChatUiRentalTravelerPolicy | undefined;
  layout: OfferLayout;
  onSelect: SelectOffer | undefined;
}>): ReactNode {
  const { rentalRefine } = useGenUiConfig();
  const [listOpen, setListOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const close = () => {
    setListOpen(false);
  };
  // A escolha na tela em diálogo também a fecha: a escolha encerra a página.
  const select: SelectOffer | undefined =
    onSelect &&
    ((offer, position, page) => {
      onSelect(offer, position, page);
      close();
    });
  return (
    <>
      <RentalOffersFooter
        layout={layout}
        hasPolicies={Boolean(policies)}
        onPolicies={() => {
          setPolicyOpen(true);
        }}
        onSeeAll={
          rentalRefine &&
          (() => {
            setListOpen(true);
          })
        }
      />
      {listOpen && (
        // A busca nova é uma tela em diálogo nova: as páginas acumuladas e a
        // resposta em voo são da busca anterior (o hook do kit não reage ao contexto).
        <RentalResultsDialog
          key={searchContext}
          offers={offers}
          metadata={metadata}
          searchContext={searchContext}
          policies={policies}
          onReserve={select}
          onClose={close}
        />
      )}
      {policyOpen && policies && (
        <RentalPolicyDrawer
          policies={policies}
          onClose={() => {
            setPolicyOpen(false);
          }}
        />
      )}
    </>
  );
}

/** O escolhido do recibo, quando o bloco o traz entre os cards. */
function chosenIdOf(
  offers: readonly ChatUiRentalOffer[],
  selectedOfferId: string | undefined,
): string | undefined {
  return offers.find((offer) => offer.offerId === selectedOfferId)?.offerId;
}

/**
 * A pilha do recibo. Ao vivo ele nasce ABERTO e encolhe no quadro seguinte —
 * o gesto do turno da escolha, também quando o host remonta o card em vez de
 * trocá-lo no lugar; remontado do histórico (`shrink` falso), já nasce
 * empilhado.
 */
function useShrinkingDeck(count: number, shrink: boolean) {
  const deck = useStackedDeck(count, '[data-deck-card]', shrink, STACK_STEP_PX);
  // Só na montagem: depois, abrir e fechar a pilha é da pessoa. O `shrink` e o
  // `collapse` do 1º render ficam presos para o efeito não repetir.
  const [mount] = useState(() => ({ shrink, collapse: deck.collapse }));
  useEffect(() => {
    if (!mount.shrink) return undefined;
    const frame = requestAnimationFrame(mount.collapse);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [mount]);
  return deck;
}

/** A fala do agente sobre o card, em markdown quando o host o desenha. */
function CardNote({ note }: Readonly<{ note: string }>): ReactNode {
  const { renderMarkdown } = useGenUiConfig();
  return <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>;
}

/** "Melhores carros" e, à direita, quantos carros a busca achou. */
function RentalOffersTitle({ count }: Readonly<{ count: number }>): ReactNode {
  const { t } = useGenUiI18n();
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h4 className="m-0 text-base font-semibold">{t('rentalOffers.title')}</h4>
      <span className="text-ui text-muted-foreground">
        {t(count === 1 ? 'rentalOffers.total_one' : 'rentalOffers.total_other', { count })}
      </span>
    </div>
  );
}

/**
 * Os "Melhores carros" da conversa (#5642): a fala do agente, o título com o
 * total da busca, os três carros que o agente separou (o `RentalOfferCard` da
 * bancada, com o selo do eixo e o porquê) e o rodapé com "Ver suas políticas" e
 * "Ver todos os carros", que abre a página da bancada em diálogo
 * (`RentalResultsDialog`, paginada pelo BFF via `rentalRefine`, sem LLM).
 *
 * O toque no card é a escolha (vai direto ao checkout). Escolhido, o agente
 * reemite o bloco como RECIBO (`collapsed` + `selectedOfferId`): no desktop a
 * lista encolhe numa pilha sob o escolhido (Figma, frame 05) e o deck ainda abre
 * para rever os três; no celular fica só o escolhido. Congelado sem escolha
 * (superado por outro turno), os cards ficam sem gesto.
 */
export function RentalOffersList({
  offers,
  metadata,
  searchContext,
  policies,
  note,
  variant,
  selectedOfferId,
  readOnly = false,
  replay = false,
  onSelect,
}: {
  readonly offers: ChatUiRentalOffer[];
  readonly metadata: ChatUiRentalSearchMeta;
  readonly searchContext: string;
  /** Políticas do viajante embutidas — habilitam "Ver suas políticas". */
  readonly policies?: ChatUiRentalTravelerPolicy;
  readonly note?: string;
  /** `collapsed` = o recibo pós-escolha (somente leitura). */
  readonly variant?: 'collapsed';
  /** A oferta escolhida — no topo e marcada no recibo. */
  readonly selectedOfferId?: string;
  /** O shell congelou o card (superado / conversa encerrada). */
  readonly readOnly?: boolean;
  /** O bloco remonta do histórico: o recibo nasce empilhado, sem encolher. */
  readonly replay?: boolean;
  /** O toque no card (ou o "Alugar" da tela em diálogo) → a escolha. */
  readonly onSelect?: SelectOffer;
}) {
  const i18n = useGenUiI18n();
  const { t } = i18n;
  const { ref, layout } = useOfferLayout<HTMLDivElement>();

  const receipt = variant === 'collapsed';
  const select = receipt || readOnly ? undefined : onSelect;
  const cardOffers = cardOffersOf(offers, receipt ? selectedOfferId : undefined);
  const chosenId = receipt ? chosenIdOf(cardOffers, selectedOfferId) : undefined;
  const views = rentalShortlistViews(i18n, cardOffers, metadata, select !== undefined);

  // A pilha é do desktop congelado.
  const deckOn = select === undefined && layout === 'wide' && views.length > 1;
  const deck = useShrinkingDeck(deckOn ? views.length : 0, receipt && !replay);

  const pick = (offerId: string) => {
    const index = cardOffers.findIndex((offer) => offer.offerId === offerId);
    const offer = cardOffers[index];
    if (offer && select) select(offer, index + 1);
  };
  return (
    <MotionConfig reducedMotion="user">
      <LayoutGroup>
        <div ref={ref} className={`grid gap-4 ${BLOCK_ELEVATION}`}>
          {note && <CardNote note={note} />}
          {/* Do card (ou da pilha) ao rodapé, 12px: o frame 05 do Figma. */}
          <div className="grid gap-3">
            <section aria-label={t('rentalOffers.title')} className="grid gap-3">
              <RentalOffersTitle count={metadata.countItems} />
              {layout === 'stack' ? (
                <RentalRail
                  views={views}
                  chosenId={chosenId}
                  receipt={chosenId !== undefined}
                  onSelect={pick}
                />
              ) : (
                <RentalDeckList
                  views={views}
                  layout={layout}
                  chosenId={chosenId}
                  deck={deck}
                  frozen={select === undefined}
                  onSelect={pick}
                />
              )}
            </section>
            {!(layout === 'stack' && chosenId) && (
              <RentalOffersActions
                offers={offers}
                metadata={metadata}
                searchContext={searchContext}
                policies={policies}
                layout={layout}
                onSelect={select}
              />
            )}
          </div>
        </div>
      </LayoutGroup>
    </MotionConfig>
  );
}
